// mejora-web F4: galería de fotos — portada (carrusel/hero) y ediciones de eventos.
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Upload, ArrowUp, ArrowDown, Eye, EyeOff, Edit2, Trash2, Loader2, AlertCircle, X, Sparkles, CalendarCheck, CalendarX } from 'lucide-react';

import {
  listGaleria, subirFotoGaleria, subirVideoGaleria, updateFotoGaleria, ordenarGaleria, deleteFotoGaleria, listEventosAdmin, generarFraseGaleria,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import CamposIdiomas, { LANGS } from '../../components/admin/CamposIdiomas';

export default function AdminGaleria() {
  const { t } = useTranslation();
  const [ambito, setAmbito] = useState('portada');
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState('');
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subiendo, setSubiendo] = useState(null); // {hechas, total}
  const [pct, setPct] = useState(null); // progreso de subida de un vídeo
  const [aplicarTratamiento, setAplicarTratamiento] = useState(true); // filtro de la web + marca de agua
  const [editar, setEditar] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    listEventosAdmin()
      .then((r) => {
        const lista = (r.data || []).slice().sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)));
        setEventos(lista);
        if (lista[0]) setEventoId((v) => v || lista[0].id);
      })
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (ambito === 'evento' && !eventoId) { setFotos([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const r = await listGaleria(ambito, ambito === 'evento' ? eventoId : null);
      setFotos(r.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [ambito, eventoId]);
  useEffect(() => { cargar(); }, [cargar]);

  const esDiario = ambito === 'diaria' || ambito === 'video_diario';

  const onSubir = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setSubiendo({ hechas: 0, total: files.length });
    const fallos = [];
    let sinFrase = null;
    for (let i = 0; i < files.length; i++) {
      try {
        const r = ambito === 'video_diario'
          ? await subirVideoGaleria(files[i], { sinTratamiento: !aplicarTratamiento }, setPct)
          : await subirFotoGaleria(files[i], {
            ambito, eventoId: ambito === 'evento' ? eventoId : null, sinTratamiento: !aplicarTratamiento,
          });
        if (esDiario && r && r && (r.frase_ia === 'no_configurada' || r.frase_ia === 'error')) sinFrase = r.frase_ia;
      } catch (err) { fallos.push(`${files[i].name}: ${err.message}`); }
      setSubiendo({ hechas: i + 1, total: files.length });
    }
    setSubiendo(null); setPct(null);
    if (fallos.length) alert(fallos.join('\n'));
    else if (sinFrase) alert(t('admin.mw.galeria.subida_sin_frase', { motivo: sinFrase === 'no_configurada' ? t('admin.mw.galeria.ia_no_config') : t('admin.mw.galeria.ia_error') }));
    cargar();
  };

  const mover = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fotos.length) return;
    const nuevo = fotos.slice();
    [nuevo[idx], nuevo[j]] = [nuevo[j], nuevo[idx]];
    setFotos(nuevo);
    try { await ordenarGaleria(nuevo.map((f) => f.id)); } catch (err) { alert(err.message); cargar(); }
  };

  const alternarVisible = async (f) => {
    try {
      const r = await updateFotoGaleria(f.id, { visible: !f.visible });
      setFotos((fs) => fs.map((x) => (x.id === f.id ? r : x)));
    } catch (err) { alert(err.message); }
  };

  const despublicar = async (f) => {
    if (!window.confirm(t('admin.mw.galeria.confirmar_despublicar'))) return;
    try {
      const r = await updateFotoGaleria(f.id, { publicar_ya: false });
      setFotos((fs) => fs.map((x) => (x.id === f.id ? r : x)));
    } catch (err) { alert(err.message); }
  };

  const publicarHoy = async (f) => {
    if (!window.confirm(t('admin.mw.galeria.confirmar_publicar_hoy'))) return;
    try {
      const r = await updateFotoGaleria(f.id, { publicar_ya: true });
      setFotos((fs) => fs.map((x) => (x.id === f.id ? r : x)));
    } catch (err) { alert(err.message); }
  };

  const borrar = async (f) => {
    if (!window.confirm(t('admin.mw.galeria.confirmar_borrar'))) return;
    try { await deleteFotoGaleria(f.id); cargar(); } catch (err) { alert(err.message); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.mw.galeria.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.mw.galeria.sub')}</p>
        </div>
        <button type="button" className="btn-primary" disabled={!!subiendo || (ambito === 'evento' && !eventoId)}
          onClick={() => inputRef.current && inputRef.current.click()}>
          {subiendo ? <Loader2 size={16} className="spin" /> : <Upload size={16} strokeWidth={1.75} />}
          {' '}{subiendo ? (pct !== null && ambito === 'video_diario' ? `${t('admin.mw.galeria.subiendo', subiendo)} ${pct}%` : t('admin.mw.galeria.subiendo', subiendo)) : (ambito === 'video_diario' ? t('admin.mw.galeria.subir_video') : t('admin.mw.galeria.subir'))}
        </button>
        <input ref={inputRef} type="file" accept={ambito === 'video_diario' ? 'video/mp4,video/webm,video/quicktime' : 'image/jpeg,image/png,image/webp'} multiple hidden onChange={onSubir} />
      </header>

      <div className="mw-tabs">
        <button type="button" className={'mw-tab' + (ambito === 'portada' ? ' active' : '')} onClick={() => setAmbito('portada')}>
          {t('admin.mw.galeria.tab_portada')}
        </button>
        <button type="button" className={'mw-tab' + (ambito === 'evento' ? ' active' : '')} onClick={() => setAmbito('evento')}>
          {t('admin.mw.galeria.tab_eventos')}
        </button>
        <button type="button" className={'mw-tab' + (ambito === 'diaria' ? ' active' : '')} onClick={() => setAmbito('diaria')}>
          {t('admin.mw.galeria.tab_diaria')}
        </button>
        <button type="button" className={'mw-tab' + (ambito === 'video_diario' ? ' active' : '')} onClick={() => setAmbito('video_diario')}>
          {t('admin.mw.galeria.tab_video')}
        </button>
        {ambito === 'evento' && (
          <select className="mw-select" value={eventoId} onChange={(e) => setEventoId(e.target.value)}>
            {eventos.map((ev) => <option key={ev.id} value={ev.id}>{ev.fecha} · {ev.nombre_es}</option>)}
          </select>
        )}
      </div>
      <p className="mw-ayuda">{ambito === 'portada' ? t('admin.mw.galeria.ayuda_portada') : ambito === 'diaria' ? t('admin.mw.galeria.ayuda_diaria') : ambito === 'video_diario' ? t('admin.mw.galeria.ayuda_video') : t('admin.mw.galeria.ayuda_evento')}</p>
      {esDiario && fotos.length > 0 && (
        <p className="mw-ayuda"><strong>{t('admin.mw.galeria.cola_info', { n: fotos.filter((f) => f.visible && !f.publicada_el).length })}</strong></p>
      )}
      <label className="mw-check">
        <input type="checkbox" checked={aplicarTratamiento} disabled={!!subiendo}
          onChange={(e) => setAplicarTratamiento(e.target.checked)} /> {t('admin.mw.galeria.tratamiento')}
      </label>
      <p className="mw-ayuda">{t('admin.mw.galeria.tratamiento_ayuda')}</p>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && !error && fotos.length === 0 && <p className="mw-vacio">{t('admin.mw.galeria.vacia')}</p>}

      {!loading && !error && fotos.length > 0 && (
        <div className="mw-galeria-grid">
          {fotos.map((f, i) => (
            <figure key={f.id} className={'mw-foto' + (f.visible ? '' : ' is-oculta')}>
              <img src={f.url} alt={f.pie_es || ''} loading="lazy" />
              {ambito === 'portada' && i === 0 && f.visible && <span className="mw-foto-badge">{t('admin.mw.galeria.principal')}</span>}
              {!f.visible && <span className="mw-foto-badge mw-foto-badge--oculta">{t('admin.mw.galeria.oculta')}</span>}
              {esDiario && f.visible && (
                <span className={'mw-foto-badge' + (f.publicada_el ? '' : ' mw-foto-badge--oculta')}>
                  {f.publicada_el ? t('admin.mw.galeria.publicada', { fecha: f.publicada_el }) : t('admin.mw.galeria.en_cola')}
                </span>
              )}
              <figcaption>
                {esDiario
                  ? (f.frase_es || <em>{t('admin.mw.galeria.sin_frase')}</em>)
                  : (f.pie_es || <em>{t('admin.mw.galeria.sin_pie')}</em>)}
              </figcaption>
              <div className="mw-foto-acciones">
                <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} aria-label={t('admin.mw.subir_orden')}><ArrowUp size={15} /></button>
                <button type="button" onClick={() => mover(i, 1)} disabled={i === fotos.length - 1} aria-label={t('admin.mw.bajar_orden')}><ArrowDown size={15} /></button>
                {esDiario && f.visible && !f.publicada_el && (
                  <button type="button" onClick={() => publicarHoy(f)} title={t('admin.mw.galeria.publicar_hoy')} aria-label={t('admin.mw.galeria.publicar_hoy')}><CalendarCheck size={15} /></button>
                )}
                {esDiario && f.visible && f.publicada_el && (
                  <button type="button" onClick={() => despublicar(f)} title={t('admin.mw.galeria.despublicar')} aria-label={t('admin.mw.galeria.despublicar')}><CalendarX size={15} /></button>
                )}
                <button type="button" onClick={() => alternarVisible(f)} aria-label={f.visible ? t('admin.mw.ocultar') : t('admin.mw.mostrar')}>
                  {f.visible ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button type="button" onClick={() => setEditar(f)} aria-label={t('admin.editar')}><Edit2 size={15} /></button>
                <button type="button" onClick={() => borrar(f)} aria-label={t('admin.borrar')}><Trash2 size={15} /></button>
              </div>
            </figure>
          ))}
        </div>
      )}

      {editar && (editar.ambito === 'diaria' || editar.ambito === 'video_diario') && (
        <FraseModal
          foto={editar}
          onCerrar={() => setEditar(null)}
          onGuardado={(r) => { setEditar(null); setFotos((fs) => fs.map((x) => (x.id === r.id ? r : x))); }}
        />
      )}
      {editar && editar.ambito !== 'diaria' && editar.ambito !== 'video_diario' && (
        <PieModal
          foto={editar}
          onCerrar={() => setEditar(null)}
          onGuardado={(r) => { setEditar(null); setFotos((fs) => fs.map((x) => (x.id === r.id ? r : x))); }}
        />
      )}
    </div>
  );
}

// «Foto del día»: frase de filosofía de vida y yoga en 4 idiomas, con botón para generarla con IA.
function FraseModal({ foto, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => Object.fromEntries(LANGS.map((l) => [`frase_${l}`, foto[`frase_${l}`] || ''])));
  const [enviando, setEnviando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);

  const generar = async () => {
    setGenerando(true); setError(null);
    try {
      const r = await generarFraseGaleria(foto.id);
      setForm(Object.fromEntries(LANGS.map((l) => [`frase_${l}`, r[`frase_${l}`] || ''])));
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'FRASE_IA_NO_CONFIGURADA' ? t('admin.mw.galeria.ia_no_config') : t('admin.mw.galeria.ia_error'));
    } finally { setGenerando(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setEnviando(true); setError(null);
    try { onGuardado(await updateFotoGaleria(foto.id, form)); }
    catch (err) { setError(err instanceof ApiError ? err.message : t('admin.error_generico')); }
    finally { setEnviando(false); }
  };
  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && !generando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.mw.galeria.editar_frase')}</h2>
        <img src={foto.url} alt="" className="mw-modal-preview" />
        <form className="modal-form" onSubmit={submit}>
          <div className="modal-acciones" style={{ justifyContent: 'flex-start', marginBottom: 8 }}>
            <button type="button" className="btn-secondary" onClick={generar} disabled={generando || enviando}>
              {generando ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} strokeWidth={1.75} />}
              {' '}{generando ? t('admin.mw.galeria.generando_ia') : t('admin.mw.galeria.generar_ia')}
            </button>
          </div>
          <div className="admin-form-grid">
            <CamposIdiomas prefijo="frase" etiqueta={t('admin.mw.galeria.frase')} form={form} setForm={setForm} multilinea filas={2} />
          </div>
          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando || generando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function PieModal({ foto, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => Object.fromEntries(LANGS.map((l) => [`pie_${l}`, foto[`pie_${l}`] || ''])));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setEnviando(true); setError(null);
    try { onGuardado(await updateFotoGaleria(foto.id, form)); }
    catch (err) { setError(err instanceof ApiError ? err.message : t('admin.error_generico')); }
    finally { setEnviando(false); }
  };
  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.mw.galeria.editar_pie')}</h2>
        <img src={foto.url} alt="" className="mw-modal-preview" />
        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <CamposIdiomas prefijo="pie" etiqueta={t('admin.mw.galeria.pie')} form={form} setForm={setForm} />
          </div>
          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
